import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MdAdd, MdAddCircleOutline, MdCalendarToday } from 'react-icons/md';
import { surveyApi } from '../../../api/surveyApi';
import { helpdeskLookupApi } from '../../../api/helpdeskApi';
import { locationApi } from '../../../api/adminapi';
import ConfirmDialog from '../../../Components/ConfirmDialog';
import MultiSelectCheckboxDropdown from '../../../Components/MultiSelectCheckboxDropdown';
import './Survey.css';

const ROLE_OPTIONS = [
  'SUPER_ADMIN',
  'ADMIN',
  'USER',
  'GATEMEN',
  'REGISTERED_USER',
  'RECRUITER',
  'SR_RECRUITER',
  'HR_HEAD',
  'HIRING_MANAGER',
];
const TARGET_TYPE_OPTIONS = [
  { value: 'ROLE', label: 'Role' },
  { value: 'DEPARTMENT', label: 'Department' },
];
const QUESTION_TYPES = ['TEXT', 'RATING', 'SINGLE_CHOICE', 'MULTI_CHOICE', 'DROPDOWN'];
const OPTION_BASED_TYPES = new Set(['SINGLE_CHOICE', 'MULTI_CHOICE', 'DROPDOWN']);

// The backend stores an empty location list to mean "not location-specific" (every location,
// including ones added later). Resolving that into "every currently active location selected"
// on load is what makes the Select All checkbox come up checked for those surveys.
const toSelectedLocations = (surveyLocations, allLocationNames) => {
  const raw = Array.isArray(surveyLocations)
    ? surveyLocations
    : (surveyLocations ? [surveyLocations] : []);
  const trimmed = raw.map((value) => String(value || '').trim()).filter(Boolean);
  return trimmed.length ? trimmed : [...allLocationNames];
};

// Selecting every currently active location is submitted as "all locations" (empty list) rather
// than a fixed list, so the survey also covers any location added after it's created.
const toLocationsPayload = (selectedLocations, allLocationNames) => (
  selectedLocations.length && selectedLocations.length === allLocationNames.length
    ? []
    : selectedLocations
);

const formatRoleLabel = (role) => role.replace(/_/g, ' ');

const getDuplicateDisplayOrders = (questionList) => {
  const counts = {};
  (questionList || []).forEach((question) => {
    const order = Number(question?.displayOrder);
    if (!Number.isFinite(order)) return;
    counts[order] = (counts[order] || 0) + 1;
  });

  return new Set(
    Object.keys(counts)
      .filter((key) => counts[key] > 1)
      .map(Number)
  );
};

const createBlankQuestion = () => ({
  question: '',
  type: 'TEXT',
  required: false,
  displayOrder: 1,
  optionsText: '',
});

const createSurveyFormSnapshot = ({ title, validTillDateTime, locations, targetType, targetValues, questions }) => {
  const normalizedQuestions = (questions || []).map((question) => ({
    question: String(question?.question || '').trim(),
    type: question?.type || 'TEXT',
    required: Boolean(question?.required),
    displayOrder: Number(question?.displayOrder || 0),
    optionsText: String(question?.optionsText || '').trim(),
  }));

  return JSON.stringify({
    title: String(title || '').trim(),
    validTillDateTime: String(validTillDateTime || ''),
    locations: Array.isArray(locations) ? [...locations].sort() : [],
    targetType: String(targetType || ''),
    targetValues: Array.isArray(targetValues) ? [...targetValues].sort() : [],
    questions: normalizedQuestions,
  });
};

const formatDateTimeForApi = (dateTimeValue) => {
  if (!dateTimeValue) return '';
  if (dateTimeValue.length === 16) {
    return `${dateTimeValue}:00`;
  }
  return dateTimeValue;
};

// The form keeps validTill as a 'YYYY-MM-DDTHH:mm' string (what the API helper expects),
// so the picker's Date is converted at the boundary rather than changing the stored shape.
const parseDateTimeLocal = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Greys out times already past - only bites on today, since future days are always ahead of now.
const isFutureTime = (time) => new Date(time).getTime() > Date.now();

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 16);
  }

  const pad = (v) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

function SurveyCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('mode') === 'edit';
  const editingSurveyId = searchParams.get('surveyId') || '';

  const [title, setTitle] = useState('');
  const [validTillDateTime, setValidTillDateTime] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [targetType, setTargetType] = useState('ROLE');
  const [targetValues, setTargetValues] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [questions, setQuestions] = useState([createBlankQuestion()]);
  const [isValidTillOpen, setIsValidTillOpen] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [initialSnapshot, setInitialSnapshot] = useState(
    createSurveyFormSnapshot({
      title: '',
      validTillDateTime: '',
      locations: [],
      targetType: 'ROLE',
      targetValues: [],
      questions: [createBlankQuestion()],
    })
  );

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const depts = await helpdeskLookupApi.getDepartments();
        setDepartments(Array.isArray(depts) ? depts : []);
      } catch (e) { console.error(e); }
    };
    loadDepartments();
  }, []);

  useEffect(() => {
    const loadLocations = async () => {
      setIsLoadingLocations(true);
      try {
        const response = await locationApi.getAllActiveLocationNames();
        const locationNames = response?.data || response || [];
        setLocations(Array.isArray(locationNames) ? locationNames : []);
      } catch (e) {
        console.error(e);
        setLocations([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };
    loadLocations();
  }, []);

  useEffect(() => {
    const loadSurveyForEdit = async () => {
      if (!isEditMode) return;

      if (!editingSurveyId) {
        toast.error('Survey ID is missing for edit mode.');
        navigate('/admin/survey/analytics', { replace: true });
        return;
      }

      setIsFormLoading(true);
      try {
        // Fetched independently of the `locations` state (set by the other effect above) so
        // resolving "blank = all locations" below doesn't race against that fetch completing.
        let allLocationNames = [];
        try {
          const locationsResponse = await locationApi.getAllActiveLocationNames();
          allLocationNames = locationsResponse?.data || locationsResponse || [];
          if (!Array.isArray(allLocationNames)) allLocationNames = [];
        } catch (e) { console.error(e); }

        const payload = await surveyApi.getSurveyById(editingSurveyId);
        const survey = payload?.data || payload;

        setTitle(String(survey?.title || '').trim());
        setValidTillDateTime(toDateTimeLocalValue(survey?.validTill || survey?.validUntil || survey?.expiryDate || survey?.endDate || ''));
        const resolvedLocations = toSelectedLocations(survey?.locations ?? survey?.location, allLocationNames);
        setSelectedLocations(resolvedLocations);

        const fetchedQuestions = Array.isArray(survey?.questions) ? survey.questions : [];
        if (!fetchedQuestions.length) {
          const blankQuestions = [createBlankQuestion()];
          setQuestions(blankQuestions);
          setInitialSnapshot(
            createSurveyFormSnapshot({
              title: String(survey?.title || '').trim(),
              validTillDateTime: toDateTimeLocalValue(survey?.validTill || survey?.validUntil || survey?.expiryDate || survey?.endDate || ''),
              locations: resolvedLocations,
              targetType: '',
              targetValues: [],
              questions: blankQuestions,
            })
          );
        } else {
          const normalizedQuestions =
            fetchedQuestions
              .slice()
              .sort((a, b) => Number(a?.displayOrder || 0) - Number(b?.displayOrder || 0))
              .map((questionItem, index) => {
                const order = Number(questionItem?.displayOrder);
                return {
                  question: String(questionItem?.question || ''),
                  type: questionItem?.type || 'TEXT',
                  required: Boolean(questionItem?.required),
                  displayOrder: Number.isFinite(order) && order > 0 ? order : index + 1,
                  optionsText: Array.isArray(questionItem?.options)
                    ? questionItem.options
                      .map((opt) => String(opt?.label || opt?.value || '').trim())
                      .filter(Boolean)
                      .join('\n')
                    : '',
                };
              });

          setQuestions(
            normalizedQuestions
          );
          setInitialSnapshot(
            createSurveyFormSnapshot({
              title: String(survey?.title || '').trim(),
              validTillDateTime: toDateTimeLocalValue(survey?.validTill || survey?.validUntil || survey?.expiryDate || survey?.endDate || ''),
              locations: resolvedLocations,
              targetType: '',
              targetValues: [],
              questions: normalizedQuestions,
            })
          );
        }
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || error.message || 'Failed to load survey details.');
        navigate('/admin/survey/analytics', { replace: true });
      } finally {
        setIsFormLoading(false);
      }
    };

    loadSurveyForEdit();
  }, [editingSurveyId, isEditMode, navigate]);

  useEffect(() => {
    if (!isEditMode) {
      setInitialSnapshot(
        createSurveyFormSnapshot({
          title: '',
          validTillDateTime: '',
          locations: [],
          targetType: 'ROLE',
          targetValues: [],
          questions: [createBlankQuestion()],
        })
      );
    }
  }, [isEditMode]);

  const handleTargetTypeChange = (nextType) => {
    setTargetType(nextType);
    setTargetValues([]);
  };

  const handleTargetValueToggle = (value) => {
    setTargetValues((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const handleQuestionChange = (index, key, value) => {
    setQuestions((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const handleAddQuestion = () => {
    setQuestions((prev) => [...prev, { ...createBlankQuestion(), displayOrder: prev.length + 1 }]);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const validatePayload = () => {
    if (!title.trim()) {
      toast.warning('Survey title is required.');
      return false;
    }

    if (!validTillDateTime) {
      toast.warning('Valid till date and time is required.');
      return false;
    }

    const validTillValue = new Date(validTillDateTime);
    if (Number.isNaN(validTillValue.getTime())) {
      toast.warning('Please select a valid date and time.');
      return false;
    }

    if (validTillValue <= new Date()) {
      toast.warning('Valid till must be a future date and time.');
      return false;
    }

    if (!selectedLocations.length) {
      toast.warning('Please select at least one location.');
      return false;
    }

    if (!isEditMode && !targetValues.length) {
      toast.warning(`Please select at least one ${targetType === 'DEPARTMENT' ? 'department' : 'role'}.`);
      return false;
    }

    if (!questions.length) {
      toast.warning('Add at least one question.');
      return false;
    }

    const hasInvalidQuestion = questions.some((question) => !question.question.trim());
    if (hasInvalidQuestion) {
      toast.warning('Each question must have text.');
      return false;
    }

    for (const question of questions) {
      const order = Number(question.displayOrder);
      if (!Number.isInteger(order) || order <= 0) {
        toast.warning('Display order must be a positive whole number for all questions.');
        return false;
      }
    }

    if (getDuplicateDisplayOrders(questions).size > 0) {
      toast.warning('Display Order already exists. Please select a unique display order.');
      return false;
    }

    for (const question of questions) {
      if (!OPTION_BASED_TYPES.has(question.type)) continue;

      const options = String(question.optionsText || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      if (options.length < 2) {
        toast.warning('Choice and dropdown questions require at least 2 options.');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePayload()) {
      return;
    }

    const requestPayload = {
      title: title.trim(),
      validTill: formatDateTimeForApi(validTillDateTime),
      locations: toLocationsPayload(selectedLocations, locations),
      questions: questions
        .map((question) => {
          const options = String(question.optionsText || '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);

          return {
            question: question.question.trim(),
            type: question.type,
            required: Boolean(question.required),
            displayOrder: Number(question.displayOrder),
            options: OPTION_BASED_TYPES.has(question.type)
              ? options.map((option) => ({
                label: option,
                value: option,
              }))
              : [],
          };
        })
        .sort((a, b) => a.displayOrder - b.displayOrder),
      ...(!isEditMode ? { targetType, targetValues } : {}),
    };

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        const updated = await surveyApi.updateSurvey(editingSurveyId, requestPayload);
        if (updated?.questionsLocked) {
          // Only a partial update landed - surface it in red so the discarded question
          // edits aren't mistaken for saved ones.
          toast.error(
            'Survey updated, but questions were not modified because this survey already has responses.',
            { autoClose: 8000 }
          );
        } else {
          toast.success('Survey updated successfully.');
        }
        navigate('/admin/survey/analytics', { state: { refreshAnalytics: true } });
      } else {
        await surveyApi.createSurvey(requestPayload);
        toast.success('Survey created successfully.');

        setTitle('');
        setValidTillDateTime('');
        setSelectedLocations([]);
        setTargetType('ROLE');
        setTargetValues([]);
        setQuestions([createBlankQuestion()]);
        setInitialSnapshot(
          createSurveyFormSnapshot({
            title: '',
            validTillDateTime: '',
            locations: [],
            targetType: 'ROLE',
            targetValues: [],
            questions: [createBlankQuestion()],
          })
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || 'Failed to create survey.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const duplicateDisplayOrders = getDuplicateDisplayOrders(questions);
  const currentSnapshot = createSurveyFormSnapshot({
    title,
    validTillDateTime,
    locations: selectedLocations,
    targetType: isEditMode ? '' : targetType,
    targetValues: isEditMode ? [] : targetValues,
    questions,
  });
  const hasUnsavedChanges = currentSnapshot !== initialSnapshot;

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
      return;
    }
    navigate('/admin/survey/my-surveys');
  };

  const confirmDiscardChanges = () => {
    setShowCancelConfirm(false);
    navigate('/admin/survey/my-surveys');
  };

  if (isFormLoading) {
    return (
      <div className="survey-create-page">
        <div className="survey-create-shell">
          <div className="survey-create-head">
            <h2>Edit Survey</h2>
            <p>Loading survey details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-create-page">
      <div className="survey-create-shell">
        <div className="survey-create-head">
          <h2>{isEditMode ? 'Edit Survey' : 'Create Survey'}</h2>
          <p>{isEditMode ? 'Update the survey details and save changes.' : 'Fill the details below and submit in one step.'}</p>
        </div>

        <form className="survey-create-form" onSubmit={handleSubmit}>
          <div className="survey-form-group">
            <label htmlFor="survey-title">Survey Title <span className="required">*</span></label>
            <input
              id="survey-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Feedback survey"
            />
          </div>

          <div className="survey-form-group">
            <label htmlFor="survey-valid-till">
              <MdCalendarToday className="survey-label-icon" />
              Valid Till (IST) <span className="required">*</span>
            </label>
            {/* Wrapper keeps react-datepicker's injected tab-loop div out of the form group's
                flex gap - without it, opening the calendar adds a gap and shifts the fields
                below down. */}
            <div className="survey-datetime-field">
              <DatePicker
                id="survey-valid-till"
                selected={parseDateTimeLocal(validTillDateTime)}
                onChange={(date) => setValidTillDateTime(date ? toDateTimeLocalValue(date) : '')}
                showTimeSelect
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="MMM d, yyyy  h:mm aa"
                minDate={new Date()}
                filterTime={isFutureTime}
                placeholderText="Select date and time"
                className="survey-datetime-input"
                calendarClassName="survey-datepicker"
                popperPlacement="bottom-start"
                /* Fixed strategy escapes the page/modal overflow that was clipping the
                   calendar, and lets floating-ui flip it above the field near the viewport
                   bottom instead of running off the page. */
                popperProps={{ strategy: 'fixed' }}
                autoComplete="off"
                open={isValidTillOpen}
                onInputClick={() => setIsValidTillOpen(true)}
                onFocus={() => setIsValidTillOpen(true)}
                onClickOutside={() => setIsValidTillOpen(false)}
                shouldCloseOnSelect={false}
              >
                <div className="survey-datepicker-footer">
                  <button
                    type="button"
                    className="survey-datepicker-btn"
                    onClick={() => {
                      setValidTillDateTime('');
                      setIsValidTillOpen(false);
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="survey-datepicker-btn survey-datepicker-btn--primary"
                    onClick={() => setIsValidTillOpen(false)}
                  >
                    OK
                  </button>
                </div>
              </DatePicker>
            </div>
          </div>

          <div className="survey-form-group">
            <label htmlFor="survey-location">Location <span className="required">*</span></label>
            <MultiSelectCheckboxDropdown
              id="survey-location"
              options={locations}
              selected={selectedLocations}
              onChange={setSelectedLocations}
              placeholder="-- Select Location --"
              allLabel="All Locations"
              loading={isLoadingLocations}
            />
          </div>

          {!isEditMode && (
            <div className="survey-form-group">
              <label>Show Survey To <span className="required">*</span></label>
              <div className="survey-role-grid survey-target-type-grid">
                {TARGET_TYPE_OPTIONS.map((option) => (
                  <label key={option.value} className="survey-role-item">
                    <input
                      type="radio"
                      name="survey-target-type"
                      checked={targetType === option.value}
                      onChange={() => handleTargetTypeChange(option.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>

              <label>
                {targetType === 'DEPARTMENT' ? 'Departments' : 'Roles'} <span className="required">*</span>
              </label>
              <div className="survey-role-grid">
                {(targetType === 'DEPARTMENT' ? departments : ROLE_OPTIONS).map((value) => (
                  <label key={value} className="survey-role-item">
                    <input
                      type="checkbox"
                      checked={targetValues.includes(value)}
                      onChange={() => handleTargetValueToggle(value)}
                    />
                    <span>{targetType === 'DEPARTMENT' ? value : formatRoleLabel(value)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="survey-form-group">
            <div className="survey-question-header">
              <label>Questions <span className="required">*</span></label>
            </div>

            <div className="survey-question-list">
              {questions.map((question, index) => {
                const isDuplicateOrder = duplicateDisplayOrders.has(Number(question.displayOrder));
                return (
                <div className="survey-question-card" key={`question-${index}`}>
                  <div className="survey-question-top">
                    <strong>Question {index + 1}</strong>
                    <button
                      type="button"
                      className="survey-remove-btn"
                      onClick={() => handleRemoveQuestion(index)}
                      disabled={questions.length === 1}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="survey-question-grid">
                    <div className="survey-form-group compact">
                      <label>Question <span className="required">*</span></label>
                      <input
                        type="text"
                        value={question.question}
                        onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                        placeholder="How satisfied are you with your work?"
                      />
                    </div>

                    <div className="survey-form-group compact">
                      <label>Type</label>
                      <select
                        value={question.type}
                        onChange={(e) => handleQuestionChange(index, 'type', e.target.value)}
                      >
                        {QUESTION_TYPES.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div className="survey-form-group compact">
                      <label>Display Order <span className="required">*</span></label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className={isDuplicateOrder ? 'survey-input-error' : undefined}
                        value={question.displayOrder}
                        onChange={(e) => handleQuestionChange(index, 'displayOrder', e.target.value)}
                        placeholder="1"
                      />
                      {isDuplicateOrder && (
                        <small className="survey-duplicate-error">Display Order already exists. Please select a unique display order.</small>
                      )}
                    </div>
                  </div>

                  <label className="survey-required-check">
                    <input
                      type="checkbox"
                      checked={question.required}
                      onChange={(e) => handleQuestionChange(index, 'required', e.target.checked)}
                    />
                    <span>Required</span>
                  </label>

                  {OPTION_BASED_TYPES.has(question.type) && (
                    <div className="survey-form-group compact">
                      <label>Options <span className="required">*</span></label>
                      <textarea
                        rows={4}
                        value={question.optionsText}
                        onChange={(e) => handleQuestionChange(index, 'optionsText', e.target.value)}
                        placeholder={'Option 1\nOption 2\nOption 3'}
                      />
                      <small className="survey-info-line">Enter one option per line.</small>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          <div className="survey-submit-row survey-submit-row-split">
            <button type="button" className="btn-secondary btn-add-green" onClick={handleAddQuestion}>
              <MdAdd />
              <span>Add Question</span>
            </button>
            <div className="survey-submit-actions-right">
              <button type="button" className="btn-secondary survey-cancel-btn" onClick={handleCancel} disabled={isSubmitting}>
                Cancel
              </button>
              <button className="btn-primary" type="submit" disabled={isSubmitting}>
                <MdAddCircleOutline />
                <span>
                  {isSubmitting
                    ? (isEditMode ? 'Saving...' : 'Creating...')
                    : (isEditMode ? 'Save Changes' : 'Create Survey')}
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={showCancelConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to cancel?"
        confirmLabel="Discard Changes"
        cancelLabel="Keep Editing"
        danger
        onConfirm={confirmDiscardChanges}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  );
}

export default SurveyCreate;
